// Client-side workout reminders using the Web Notifications API.
// Preferences are stored in localStorage; the scheduler uses setTimeout while
// the app is open. For "reminders when app is closed", a Service Worker + Push
// backend would be needed — this simpler approach still delivers real value
// for users who keep the tab open.

const KEY = "fc_reminders_v1";

const DEFAULT = {
  enabled: false,
  days: [1, 3, 5], // 0=Dom, 1=Lun ... 6=Sab
  time: "18:00", // HH:MM
  lastNotifiedISO: null,
};

export function loadReminderPrefs() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULT };
    return { ...DEFAULT, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULT };
  }
}

export function saveReminderPrefs(prefs) {
  localStorage.setItem(KEY, JSON.stringify(prefs));
}

export function notificationPermission() {
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission; // "default" | "granted" | "denied"
}

export async function requestNotificationPermission() {
  if (typeof Notification === "undefined") return "unsupported";
  if (Notification.permission === "granted") return "granted";
  const r = await Notification.requestPermission();
  return r;
}

function fireNotification(title, body) {
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
  try {
    const n = new Notification(title, {
      body,
      icon: "/favicon.ico",
      tag: "fitcommand-reminder",
    });
    n.onclick = () => {
      window.focus();
      n.close();
    };
  } catch {
    // ignore (some browsers throw when tab is in background)
  }
}

// Compute the next occurrence (Date) matching prefs, starting from `from`.
function nextReminderTime(prefs, from = new Date()) {
  if (!prefs.enabled || !prefs.days || prefs.days.length === 0) return null;
  const [hh, mm] = (prefs.time || "18:00").split(":").map((s) => parseInt(s, 10));
  for (let offset = 0; offset < 8; offset++) {
    const d = new Date(from);
    d.setDate(from.getDate() + offset);
    d.setHours(hh, mm, 0, 0);
    if (prefs.days.includes(d.getDay()) && d.getTime() > from.getTime()) return d;
  }
  return null;
}

let _timerId = null;

export function stopReminderScheduler() {
  if (_timerId) {
    clearTimeout(_timerId);
    _timerId = null;
  }
}

// Start (or restart) the client-side scheduler. Idempotent.
export function startReminderScheduler(getUserName = () => "atleta") {
  stopReminderScheduler();
  const prefs = loadReminderPrefs();
  if (!prefs.enabled) return;
  if (notificationPermission() !== "granted") return;

  const scheduleNext = () => {
    const next = nextReminderTime(prefs);
    if (!next) return;
    const delay = Math.max(1000, next.getTime() - Date.now());
    _timerId = setTimeout(() => {
      const today = new Date().toISOString().slice(0, 10);
      const last = loadReminderPrefs().lastNotifiedISO;
      if (last !== today) {
        const name = getUserName();
        const msg = `${name}, è ora di allenarti. FitCommand è pronto.`;
        fireNotification("⚡ Allenamento in agenda", msg);
        saveReminderPrefs({ ...prefs, lastNotifiedISO: today });
      }
      // Schedule the next one recursively
      scheduleNext();
    }, delay);
  };

  scheduleNext();
}

// Fire a quick test notification (to preview the setup).
export function testNotification() {
  fireNotification("🔔 Test promemoria", "Il tuo promemoria FitCommand funziona. A presto!");
}
